import { useRef, useEffect, useContext, useState } from 'react'
import { createPlayer, loadMedia, PlayerInterface } from '@foundation-player/api'
import { ConsentContext } from '@/context/ConsentContext'
import { type VideoPlayerProps } from '@/types/video'
import { createLoadRequest } from './config'
import styles from './VideoPlayer.module.scss'
import { MetaDataContext } from '@/context/MetadataContext'
import { debug } from '@/utils/common'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $AC: any
  }
}

const VideoPlayer = ({
  props,
  providePlayer,
}: {
  props: VideoPlayerProps
  providePlayer?: (player: PlayerInterface) => void
}) => {
  const consentContext = useContext(ConsentContext)
  const metaData = useContext(MetaDataContext)
  const [acReady, setAcReady] = useState(false)
  const [player, setPlayer] = useState<PlayerInterface | undefined>(undefined)
  const elem = useRef<HTMLDivElement>(null)

  async function createPlayerUIAndLoadMedia() {
    try {
      if (elem.current && !player) {
        elem.current.innerHTML = ''

        const mountPoint = elem.current
        const loadRequest = createLoadRequest({
          videoProps: props,
          isPurMode: consentContext.status === 'purMode',
          isPreview: metaData?.model.isPreview ?? false,
          consentString: consentContext.tcString,
        })
        debug('videoplayer/loadRequest', loadRequest)

        const pl = await createPlayer({
          mountPoint,
          loadRequest,
          ui: {
            enabled: true,
            features: {
              fullscreenOnlyEmitsEvents: true,
            },
          },
        })

        let originalParent: Element | null = null
        let originalNextSibling: ChildNode | null = null

        pl.addEventListener('ui:fullscreen:enterRequest', () => {
          originalParent = mountPoint.parentElement
          originalNextSibling = mountPoint.nextSibling
          document.body.appendChild(mountPoint)
          mountPoint.classList.add(styles['alt-fullscreen'])
        })
        pl.addEventListener('ui:ExitFullscreenRequest', () => {
          mountPoint.classList.remove(styles['alt-fullscreen'])
          if (originalParent) {
            originalParent.insertBefore(mountPoint, originalNextSibling)
          }
        })

        setPlayer(pl)

        if (providePlayer) providePlayer(pl)
      }
    } catch (e) {
      console.log('Error:', e)
    }
  }
  useEffect(() => {
    // if new props and player exist load a new request
    if (player) {
      const loadRequest = createLoadRequest({
        videoProps: props,
        isPurMode: consentContext.status === 'purMode',
        isPreview: metaData?.model.isPreview ?? false,
      })
      debug('videoplayer/playlist loadRequest', loadRequest)
      loadMedia(player, loadRequest)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props])

  useEffect(() => {
    if (!player && consentContext.consentReady && (consentContext.status === 'purMode' || acReady))
      createPlayerUIAndLoadMedia()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acReady, consentContext])

  useEffect(() => {
    let timeout: undefined | number = undefined

    const listener = () => {
      clearTimeout(timeout)
      setAcReady(true)
    }

    if (window.$AC && typeof window.$AC.playerInitReady === 'function' && window.$AC.playerInitReady()) {
      setAcReady(true)
    } else {
      window.addEventListener('AC_player_start', listener)
      // fallback
      timeout = window.setTimeout(function () {
        window.removeEventListener('AC_player_start', listener)
        setAcReady(true)
      }, 3000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={styles.wrapper}>
      {props.poster && <img src={props.poster} alt="Video poster" />}
      <div className={styles.container} ref={elem}>
        <video preload="none" width="100%" poster={props.poster} controls>
          {props.source.progressive && <source src={props.source.progressive} id="mp4" type="video/mp4" />}
          {props.source.hls && <source src={props.source.hls} id="m3u8" type="application/vnd.apple.mpegURL" />}
          <track default kind="captions" srcLang="de" />
        </video>
      </div>
    </div>
  )
}

export default VideoPlayer
